import { useMemo, useState } from "react";
import { Lightning } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { ProjectBlockersFeed } from "@/components/ProjectBlockersFeed";
import { blockerRows, useAttention } from "@/lib/blockers";
import { projectInstall } from "@/pages/Projects";

const VIEWS = [
  ["all", "All blocked"],
  ["week", "Installing this week"],
  ["hold", "On hold"],
  ["unscheduled", "Not scheduled"],
];

export default function NeedsAPush() {
  const { projects, conflicts, loading } = useAttention(true);
  const [view, setView] = useState("all");

  const rows = useMemo(() => blockerRows(projects, conflicts), [projects, conflicts]);
  const counts = useMemo(() => {
    const c = { all: rows.length, week: 0, hold: 0, unscheduled: 0 };
    rows.forEach(({ p }) => {
      const inst = projectInstall(p);
      if (inst && inst.days >= 0 && inst.days <= 7) c.week += 1;
      if (p.hold_status) c.hold += 1;
      if (!inst) c.unscheduled += 1;
    });
    return c;
  }, [rows]);

  const shown = useMemo(
    () =>
      projects.filter((p) => {
        if (!rows.some((r) => r.p.id === p.id)) return true;
        const inst = projectInstall(p);
        if (view === "week") return inst && inst.days >= 0 && inst.days <= 7;
        if (view === "hold") return !!p.hold_status;
        if (view === "unscheduled") return !inst;
        return true;
      }),
    [projects, rows, view],
  );
  const total = rows.reduce((n, r) => n + r.blocks.length, 0);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <AppHeader title="Needs a push" />
      <main className="app-shell-offset mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8" data-testid="needs-push-page">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <Lightning size={12} weight="fill" className="text-amber-300" /> Projects
            </div>
            <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Needs a push</h1>
            <p className="mt-1 text-sm text-zinc-500" data-testid="needs-push-summary">
              {loading
                ? "Checking every active project…"
                : rows.length
                  ? `${total} blocker${total === 1 ? "" : "s"} across ${rows.length} project${rows.length === 1 ? "" : "s"} — soonest install first.`
                  : "Every active project is moving."}
            </p>
          </div>
          <div className="ml-auto flex flex-wrap gap-1.5" data-testid="needs-push-views">
            {VIEWS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-testid={`needs-push-view-${k}`}
                onClick={() => setView(k)}
                className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  view === k
                    ? "border-indigo-400/60 bg-indigo-500/15 text-indigo-200"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"
                }`}
              >
                {label}
                <span className={`font-mono-data rounded-full px-1.5 text-[9px] ${view === k ? "bg-indigo-400/30" : "bg-zinc-800"}`}>
                  {counts[k]}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <ProjectBlockersFeed projects={shown} conflicts={conflicts} loading={loading} full />
        </div>
      </main>
    </div>
  );
}
